import { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Popup, Circle } from 'react-leaflet';
import { Icon } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Sidebar } from '../../../components/feature/Sidebar';
import { getMockAnimals, getMockLocationData } from '../../../data/farmtraceMockData';

// Fix for default marker icon in React-Leaflet
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete (Icon.Default.prototype as any)._getIconUrl;
Icon.Default.mergeOptions({
    iconUrl: markerIcon,
    iconRetinaUrl: markerIcon2x,
    shadowUrl: markerShadow,
});

interface AnimalLocation {
    id: string;
    name: string;
    lat: number;
    lng: number;
    status: 'healthy' | 'attention' | 'critical';
    lastUpdate: string;
    milkProduction?: number;
}

export default function FarmTraceOperations() {
    const [loading, setLoading] = useState(true);
    const [animalLocations, setAnimalLocations] = useState<AnimalLocation[]>([]);
    const [selectedAnimal, setSelectedAnimal] = useState<AnimalLocation | null>(null);
    const [mapCenter, setMapCenter] = useState<[number, number]>([-33.4489, -70.6693]); // Santiago, Chile default
    const [mapReady, setMapReady] = useState(false);
    const [filterStatus, setFilterStatus] = useState<'all' | 'healthy' | 'attention' | 'critical'>('all');

    useEffect(() => {
        loadOperationalData();
    }, []);

    const loadOperationalData = async () => {
        try {
            setLoading(true);
            const [animalsRes, locationsRes] = await Promise.allSettled([
                getMockAnimals(),
                getMockLocationData().then(data => [data]),
            ]);

            const animalsData = animalsRes.status === 'fulfilled' ? extractArray(animalsRes.value) : [];
            const locationsData = locationsRes.status === 'fulfilled' ? extractArray(locationsRes.value) : [];

            // Determinar el centro del mapa
            let centerLat = -33.4489;
            let centerLng = -70.6693;

            if (locationsData.length > 0 && locationsData[0]) {
                centerLat = locationsData[0].latitude || centerLat;
                centerLng = locationsData[0].longitude || centerLng;
            }

            // Simular ubicaciones georreferenciadas para las vacas
            const locations: AnimalLocation[] = animalsData.slice(0, 20).map((animal: any, index: number) => {
                return {
                    id: animal.animalId || `animal-${index}`,
                    name: animal.name || `Vaca ${animal.animalId || index}`,
                    lat: centerLat + (Math.random() - 0.5) * 0.01,
                    lng: centerLng + (Math.random() - 0.5) * 0.01,
                    status: Math.random() > 0.8 ? 'attention' : Math.random() > 0.95 ? 'critical' : 'healthy',
                    lastUpdate: new Date().toISOString(),
                    milkProduction: Math.floor(Math.random() * 30) + 10,
                };
            });

            setAnimalLocations(locations);
            setMapCenter([centerLat, centerLng]);
            setMapReady(true);
        } catch (error) {
            console.error('Error loading operational data:', error);
            // Asegurar que el mapa esté listo incluso si hay error
            setMapReady(true);
        } finally {
            setLoading(false);
        }
    };

    const extractArray = (response: any): any[] => {
        if (Array.isArray(response)) return response;
        if (response?.items) return response.items;
        if (response?.member) return response.member;
        if (response?.data) return response.data;
        return [];
    };

    const getMarkerColor = (status: string) => {
        switch (status) {
            case 'healthy': return '#10b981';
            case 'attention': return '#f59e0b';
            case 'critical': return '#ef4444';
            default: return '#6b7280';
        }
    };

    const filteredLocations = animalLocations.filter(location =>
        filterStatus === 'all' || location.status === filterStatus
    );

    const statusCounts = {
        healthy: animalLocations.filter(l => l.status === 'healthy').length,
        attention: animalLocations.filter(l => l.status === 'attention').length,
        critical: animalLocations.filter(l => l.status === 'critical').length,
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="inline-block w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                    <p className="text-gray-700 text-lg font-medium">Cargando datos operacionales...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex">
            <Sidebar />

            <div className="flex-1 flex flex-col overflow-hidden ml-[var(--sidebar-width)] transition-all duration-300">
                {/* Top Bar */}
                <div className="bg-white/80 backdrop-blur-xl border-b border-gray-200 px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Operaciones Georreferenciadas</h1>
                            <p className="text-sm text-gray-600 mt-1">Ubicación en tiempo real del ganado</p>
                        </div>
                        <button
                            onClick={loadOperationalData}
                            className="p-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl hover:from-blue-600 hover:to-indigo-600 transition-all duration-200 shadow-lg"
                            title="Actualizar datos"
                        >
                            <i className="ri-refresh-line text-lg"></i>
                        </button>
                    </div>
                </div>

                {/* Main Content */}
                <div className="flex-1 overflow-y-auto p-8">
                    {/* Stats Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Total Animales</p>
                                    <p className="text-3xl font-bold text-gray-900">{animalLocations.length}</p>
                                </div>
                                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                                    <i className="ri-map-pin-line text-blue-600 text-2xl"></i>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Saludables</p>
                                    <p className="text-3xl font-bold text-green-600">{statusCounts.healthy}</p>
                                </div>
                                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                                    <i className="ri-heart-pulse-line text-green-600 text-2xl"></i>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Requiere Atención</p>
                                    <p className="text-3xl font-bold text-yellow-600">{statusCounts.attention}</p>
                                </div>
                                <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                                    <i className="ri-alert-line text-yellow-600 text-2xl"></i>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Estado Crítico</p>
                                    <p className="text-3xl font-bold text-red-600">{statusCounts.critical}</p>
                                </div>
                                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                                    <i className="ri-error-warning-line text-red-600 text-2xl"></i>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Map and Details */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Map */}
                        <div className="lg:col-span-2 bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
                            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                                <h2 className="text-lg font-bold text-gray-900">Mapa de Ubicaciones</h2>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setFilterStatus('all')}
                                        className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${filterStatus === 'all'
                                            ? 'bg-blue-500 text-white'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                    >
                                        Todos
                                    </button>
                                    <button
                                        onClick={() => setFilterStatus('healthy')}
                                        className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${filterStatus === 'healthy'
                                            ? 'bg-green-500 text-white'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                    >
                                        Saludables
                                    </button>
                                    <button
                                        onClick={() => setFilterStatus('attention')}
                                        className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${filterStatus === 'attention'
                                            ? 'bg-yellow-500 text-white'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                    >
                                        Atención
                                    </button>
                                    <button
                                        onClick={() => setFilterStatus('critical')}
                                        className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${filterStatus === 'critical'
                                            ? 'bg-red-500 text-white'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                    >
                                        Crítico
                                    </button>
                                </div>
                            </div>
                            <div className="h-[600px]">
                                {mapReady && mapCenter[0] && mapCenter[1] ? (
                                    <MapContainer
                                        center={mapCenter}
                                        zoom={15}
                                        style={{ height: '100%', width: '100%' }}
                                        className="z-0"
                                        key={`${mapCenter[0]}-${mapCenter[1]}`}
                                    >
                                        <TileLayer
                                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                        />
                                        {filteredLocations.map((location) => (
                                            <Circle
                                                key={location.id}
                                                center={[location.lat, location.lng]}
                                                radius={20}
                                                pathOptions={{
                                                    color: getMarkerColor(location.status),
                                                    fillColor: getMarkerColor(location.status),
                                                    fillOpacity: 0.6,
                                                }}
                                                eventHandlers={{
                                                    click: () => setSelectedAnimal(location),
                                                }}
                                            >
                                                <Popup>
                                                    <div className="p-2">
                                                        <h3 className="font-bold text-gray-900">{location.name}</h3>
                                                        <p className="text-sm text-gray-600">ID: {location.id}</p>
                                                        <p className="text-sm">
                                                            Estado:{' '}
                                                            <span
                                                                className={`font-semibold ${location.status === 'healthy'
                                                                    ? 'text-green-600'
                                                                    : location.status === 'attention'
                                                                        ? 'text-yellow-600'
                                                                        : 'text-red-600'
                                                                    }`}
                                                            >
                                                                {location.status === 'healthy'
                                                                    ? 'Saludable'
                                                                    : location.status === 'attention'
                                                                        ? 'Requiere Atención'
                                                                        : 'Crítico'}
                                                            </span>
                                                        </p>
                                                        {location.milkProduction && (
                                                            <p className="text-sm text-gray-600">
                                                                Producción: {location.milkProduction}L/día
                                                            </p>
                                                        )}
                                                    </div>
                                                </Popup>
                                            </Circle>
                                        ))}
                                    </MapContainer>
                                ) : (
                                    <div className="h-full flex items-center justify-center bg-gray-50">
                                        <div className="text-center">
                                            <div className="inline-block w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                                            <p className="text-gray-600">Cargando mapa...</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Animal Details */}
                        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                            <h2 className="text-lg font-bold text-gray-900 mb-4">Detalles del Animal</h2>
                            {selectedAnimal ? (
                                <div className="space-y-4">
                                    <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                                        <h3 className="font-bold text-gray-900 text-lg mb-2">{selectedAnimal.name}</h3>
                                        <p className="text-sm text-gray-600">ID: {selectedAnimal.id}</p>
                                    </div>

                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                            <span className="text-sm text-gray-600">Estado</span>
                                            <span
                                                className={`px-3 py-1 rounded-full text-xs font-semibold ${selectedAnimal.status === 'healthy'
                                                    ? 'bg-green-100 text-green-700'
                                                    : selectedAnimal.status === 'attention'
                                                        ? 'bg-yellow-100 text-yellow-700'
                                                        : 'bg-red-100 text-red-700'
                                                    }`}
                                            >
                                                {selectedAnimal.status === 'healthy'
                                                    ? 'Saludable'
                                                    : selectedAnimal.status === 'attention'
                                                        ? 'Requiere Atención'
                                                        : 'Crítico'}
                                            </span>
                                        </div>

                                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                            <span className="text-sm text-gray-600">Ubicación</span>
                                            <span className="text-sm font-semibold text-gray-900">
                                                {selectedAnimal.lat.toFixed(4)}, {selectedAnimal.lng.toFixed(4)}
                                            </span>
                                        </div>

                                        {selectedAnimal.milkProduction && (
                                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                                <span className="text-sm text-gray-600">Producción Diaria</span>
                                                <span className="text-sm font-semibold text-blue-600">
                                                    {selectedAnimal.milkProduction}L
                                                </span>
                                            </div>
                                        )}

                                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                            <span className="text-sm text-gray-600">Última Actualización</span>
                                            <span className="text-sm font-semibold text-gray-900">
                                                {new Date(selectedAnimal.lastUpdate).toLocaleTimeString('es-ES')}
                                            </span>
                                        </div>
                                    </div>

                                    <button className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-semibold py-3 rounded-xl hover:from-blue-600 hover:to-indigo-600 transition-all duration-200 shadow-lg">
                                        Ver Historial Completo
                                    </button>
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <i className="ri-map-pin-line text-gray-300 text-6xl mb-4"></i>
                                    <p className="text-gray-500">Selecciona un animal en el mapa para ver sus detalles</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
